import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarClock, CheckCircle2 } from "lucide-react";
import { api, fmtUSD, fmtUSD2 } from "@/lib/apiClient";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import PeriodPicker, { presetRange } from "@/components/PeriodPicker";
import OperationsTable from "@/components/OperationsTable";

export default function Upcoming() {
  const [range, setRange] = useState({ ...presetRange("quarter"), preset: "quarter" });
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/upcoming", { params: { date_from: range.from, date_to: range.to } });
    setData(data);
  }, [range.from, range.to]);

  useEffect(() => {
    load();
  }, [load]);

  const confirmAll = async (group) => {
    await api.post("/operations/bulk-confirm", group.operations.map((o) => o.id));
    toast.success(`Подтверждено операций: ${group.operations.length}`);
    load();
  };

  const confirmOne = async (op) => {
    await api.post(`/operations/${op.id}/confirm`);
    toast.success("Операция подтверждена");
    load();
  };

  return (
    <div className="space-y-6 md:space-y-8">
      <div>
        <h1 className="text-3xl font-bold sm:text-4xl">Предстоящие выплаты</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Запланированные, но не подтверждённые расходы и переводы за выбранный период
        </p>
      </div>

      <PeriodPicker range={range} onChange={setRange} />

      <div className="grid gap-4 md:grid-cols-3">
        <Card className="glass metric-card" data-testid="upcoming-total">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Сумма к выплате</span>
              <CalendarClock className="h-4 w-4 text-warning" />
            </div>
            <div className="mt-2 font-mono text-2xl font-bold text-warning lg:text-3xl">{fmtUSD2(data?.total)}</div>
          </CardContent>
        </Card>
        <Card className="glass metric-card" data-testid="upcoming-count">
          <CardContent className="p-5">
            <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Операций</span>
            <div className="mt-2 font-mono text-2xl font-bold lg:text-3xl">{data?.count || 0}</div>
          </CardContent>
        </Card>
        <Card className="glass metric-card" data-testid="upcoming-companies">
          <CardContent className="p-5">
            <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Компаний затронуто</span>
            <div className="mt-2 font-mono text-2xl font-bold lg:text-3xl">{data?.groups?.length || 0}</div>
          </CardContent>
        </Card>
      </div>

      {!data?.groups?.length ? (
        <div className="rounded-lg border border-dashed p-10 text-center text-sm text-muted-foreground" data-testid="upcoming-empty">
          Нет запланированных выплат за выбранный период
        </div>
      ) : (
        <Accordion type="multiple" className="space-y-3" data-testid="upcoming-groups">
          {data.groups.map((g) => (
            <AccordionItem key={g.company_id} value={g.company_id} className="glass rounded-xl border px-4">
              <AccordionTrigger className="hover:no-underline" data-testid={`upcoming-group-${g.company_id}`}>
                <div className="flex w-full flex-col gap-1 pr-3 text-left sm:flex-row sm:items-center sm:justify-between">
                  <span className="text-base font-semibold">{g.company_name}</span>
                  <span className="flex items-center gap-4 text-sm">
                    <span className="text-muted-foreground">{g.count} операций</span>
                    <span className="font-mono font-bold text-warning">{fmtUSD(g.total)}</span>
                  </span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-5">
                <div className="mb-3 flex justify-end">
                  <Button size="sm" variant="outline" className="gap-2" data-testid={`confirm-group-${g.company_id}`}
                    onClick={() => confirmAll(g)}>
                    <CheckCircle2 className="h-4 w-4" /> Подтвердить все
                  </Button>
                </div>
                <OperationsTable operations={g.operations} onConfirm={confirmOne} />
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </div>
  );
}
